import { ToastAlertTypes } from '@genial/components';
import { IconographyTypes } from '@genial/icons';
import {
  FixedIncomeRequestsFixedIncomeOfferCategoriesResponse,
  ResponsesFundOfferCategoriesResponse,
  StrategyEntity,
} from '@genial/services/dist/models';
import { Utils } from '@genial/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { DimensionValue } from 'react-native';
import * as zod from 'zod';

type ProductTableItem = {
  qualifiedInvestor: boolean;
  dailyLiquidity: boolean;
  fgcProtected: boolean;
  taxExempt: boolean;
  closedFund: boolean;
  professionalInvestor: boolean;
};

export const defaultErrorMessage: [ToastAlertTypes.Type, string, string] = [
  'error',
  'Opa! Algo parece errado.',
  'Ocorreu um erro ao carregar o Portal,\nverifique sua conexão com a internet e\ntente novamente em instantes.',
];

export const notFoundErrorMessage: [ToastAlertTypes.Type, string, string] = [
  'error',
  'Produto não encontrado',
  'Não encontramos nenhum resultado correspondente à sua pesquisa. Por favor, verifique os parâmetros informados e tente novamente.',
];

export const validateEditAppliedAmount = (min?: number, max?: number, _balance?: number) => {
  const validationEditSchema = zod.object({
    editAppliedAmount: zod
      .string({
        required_error: 'Preencha o campo com um valor válido',
        invalid_type_error: 'Preencha o campo com um valor válido',
      })
      .refine((value) => Utils.Unmask.Money(value) >= (min ?? 0), {
        message: `Valor mínimo ${Utils.Mask.Money(min)}`,
      })
      .refine((value) => Utils.Unmask.Money(value) <= (max ?? 0), {
        message: `Valor máximo ${Utils.Mask.Money(max)}`,
      }),
  });

  return zodResolver(validationEditSchema);
};

export const validateFundEditAppliedAmount = (min: number) => {
  const validationCreateSchema = zod.object({
    editAppliedAmount: zod
      .string({ required_error: `Valor mínimo ${Utils.Mask.Money(min)}` })
      .refine((value) => Utils.Unmask.Money(value) >= min, {
        message: `Valor mínimo ${Utils.Mask.Money(min)}`,
      }),
  });

  return zodResolver(validationCreateSchema);
};

export const productCaptions = (obj: Partial<ProductTableItem>): IconographyTypes.Option[] => {
  const result: IconographyTypes.Option[] = [];

  if (obj.qualifiedInvestor) {
    result.push('QualifiedClient');
  }

  if (obj.closedFund) {
    result.push('Locked');
  }

  if (obj.professionalInvestor) {
    result.push('ProfessionalInvestor');
  }

  if (obj.taxExempt) {
    result.push('IncomeTaxExempt');
  }

  if (obj.dailyLiquidity) {
    result.push('Liquidity');
  }

  if (obj.fgcProtected) {
    result.push('Fgc');
  }

  return result;
};

export const minValueSearchSchema = { Equity: 5 };

export const validateFormSearchSchema = (min = 2) => {
  const formSearchSchema = zod.object({
    searchTerm: zod
      .string({ required_error: 'Campo obrigatório' })
      .min(min, `Campo deve ter no mínimo ${min} caracteres`)
      .refine((value) => value.trim() !== '', {
        message: 'Campo não pode consistir apenas de espaços em branco',
      }),
  });

  return formSearchSchema;
};

export type SearchFormData = zod.infer<ReturnType<typeof validateFormSearchSchema>>;

export const DEFAULT_PAGINATION = { page: 1, limit: 40 };

export type TabsObject = {
  [key: string]: {
    title: string;
    component?: {
      table: JSX.Element;
      captions: JSX.Element | null;
    };
    categoryIndexes?: number[];
    onFilter?: (index: number) => void;
  };
};

export type Tab = {
  title: string;
};

export const transformTabsToArray = (tabsObject: TabsObject) => {
  const tabsArray: Tab[] = [];

  for (const key in tabsObject) {
    if (Object.hasOwnProperty.call(tabsObject, key)) {
      const tab = tabsObject[key];
      tabsArray.push({ title: tab.title });
    }
  }

  return tabsArray;
};

export const maxHeight = '924px' as DimensionValue;
export const rowHeight = 80;

const mappedFieldsOrderToFix = {
  profitabilityFormatted: 'profitabilityTax',
  quotationRedemptionTermFormatted: 'quotationRedemptionTerm',
  profitabilityTwelveMonthsAdjusted: 'profitabilityTwelveMonths',
  suitabilityProfileName: 'riskRatingLevel',
  maturityDateFormatted: 'maturityDate',
  feesPayment: 'periodicityRate',
  inputApplicationAmount: 'minimumApplication',
};

export const fixOrderFields = (value: string): string => {
  // eslint-disable-next-line no-prototype-builtins
  if (mappedFieldsOrderToFix.hasOwnProperty(value)) {
    return mappedFieldsOrderToFix[value];
  }

  return value;
};

export type ItemsFindIndexes =
  | ResponsesFundOfferCategoriesResponse
  | FixedIncomeRequestsFixedIncomeOfferCategoriesResponse
  | StrategyEntity;

export const findIndexes = (
  items: ItemsFindIndexes[],
  selectedItems?: string[] | null
): number[] => {
  if (!selectedItems || selectedItems.length === 0) {
    return [0];
  }
  return items
    ?.map((item, index: number) => (selectedItems.includes(item.value as string) ? index : null))
    .filter((index) => index !== null) as number[];
};

export interface OrderByProps {
  sort: string;
  sortDirection: 'asc' | 'desc';
  orderByType: 'FixedIncome' | 'Funds' | 'Equity' | 'StructuredOperations' | 'DirectTreasury';
}

export type handleOrderByProps = (value: OrderByProps) => void;

export const calculateLength = (obj: Record<string, any>): number => {
  let length = 0;

  Object.keys(obj).forEach((key) => {
    const value = obj[key];

    if (Array.isArray(value) && value.length > 0) {
      length++;
    } else if (!Array.isArray(value) && (value || value === false)) {
      length++;
    }
  });

  return length;
};

export type dataOptionsInterface =
  | FixedIncomeRequestsFixedIncomeOfferCategoriesResponse
  | ResponsesFundOfferCategoriesResponse
  | StrategyEntity;

export const transformDataOptions = (data?: dataOptionsInterface[]) => {
  const filterOptionsData = {
    options: [],
    sideOptions: [],
  };

  data?.forEach((item, index) => {
    if (index < 4) {
      filterOptionsData.options.push(item as never);
    } else {
      filterOptionsData.sideOptions.push(item as never);
    }
  });

  return filterOptionsData as {
    options: dataOptionsInterface[];
    sideOptions: dataOptionsInterface[];
  };
};

export const createTabsObject = (
  tabNames: (string | boolean)[]
): {
  [index: number]: 'FixedIncome' | 'Fund' | 'Equity' | 'StructuredOperations' | 'DirectTreasury';
} => {
  const tabsTypes = {};

  tabNames.forEach((name, index) => {
    tabsTypes[index] = name;
  });

  return tabsTypes;
};
